import { useResendCooldown } from '@flashka/shared/react/use-resend-cooldown';
import { Button, CodeInput, Input } from '@flashka/shared/ui';

import { LogOutIcon } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { toast } from '@/lib/flashka-toast';

import { authClient, clearJwtCache } from '../client';
import { useAuthControls } from '../session';
import { useAuthAction } from '../use-auth-action';

export function AccountProfileTab() {
  const { t } = useTranslation();
  // `refetch` is the session store's own fetch: it writes `data` back, so
  // `updateUser`/`changeEmail` are reflected immediately. Needed because
  // `emailOTPClient` only fires `$sessionSignal` for verify-email / sign-in /
  // request-email-change — NOT `changeEmail` — and a bare `getSession()` is a
  // plain fetch that never touches the store. `disableCookieCache` skips the
  // server's cookie-cached (stale) session.
  const { data, refetch } = authClient.useSession();
  const user = data?.user;
  const refreshSession = () => refetch({ query: { disableCookieCache: true } });

  const [name, setName] = useState<string | null>(null); // null = not editing
  const [emailStep, setEmailStep] = useState<'idle' | 'enter' | 'otp'>('idle');
  const [newEmail, setNewEmail] = useState('');
  const [otp, setOtp] = useState('');
  const { signOut } = useAuthControls();
  const { busy, errorKey, run } = useAuthAction();
  const resend = useResendCooldown();

  // No hooks below this point — everything above must run on every render
  // regardless of session state, so the early return can't skip any of it.
  if (!user) return null;

  async function saveName(e: FormEvent) {
    e.preventDefault();
    if (name === null) return;
    if (await run(() => authClient.updateUser({ name }))) {
      clearJwtCache(); // the cached 15-min JWT still carries the old name claim
      await refreshSession();
      setName(null);
      toast(t('auth.profile.nameSaved'));
    }
  }

  // Event-free core so both the "enter new email" form submit AND the OTP
  // screen's "resend" button (which has no form event to preventDefault)
  // can call the exact same request logic.
  async function requestEmailChangeOtp() {
    if (await run(() => authClient.emailOtp.requestEmailChange({ newEmail }))) {
      resend.start();
      setOtp('');
      setEmailStep('otp');
    }
  }

  async function submitEmailChangeRequest(e: FormEvent) {
    e.preventDefault();
    await requestEmailChangeOtp();
  }

  async function confirmEmailChange(e: FormEvent) {
    e.preventDefault();
    if (await run(() => authClient.emailOtp.changeEmail({ newEmail, otp }))) {
      clearJwtCache(); // the cached 15-min JWT still carries the old email claim
      await refreshSession();
      setEmailStep('idle');
      setNewEmail('');
      setOtp('');
      toast(t('auth.profile.emailChanged'));
    }
  }

  return (
    <div className="flex flex-col gap-4 py-2">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          {user.image ? (
            <img src={user.image} alt="" className="h-12 w-12 rounded-full" />
          ) : (
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted text-lg font-semibold">
              {(user.name || user.email)[0]?.toUpperCase()}
            </div>
          )}
          <div className="min-w-0">
            <p className="font-medium">{user.name}</p>
            <p className="break-all text-sm text-muted-foreground">{user.email}</p>
          </div>
        </div>
        {/* Sign out, beside the identity (2026-09-15): the sidebar chip stopped
            opening the account menu that used to carry it (same copy key).
            Change email's size and variant, the DS label colour, a leading
            log-out glyph — no hand-tinted red (2026-09-16). */}
        <Button
          variant="secondary"
          leftIcon={LogOutIcon}
          disabled={busy}
          onPress={() => {
            void signOut();
          }}>
          {t('avatarDropdown.signOut')}
        </Button>
      </div>

      <Separator />

      <form onSubmit={saveName} className="flex items-end gap-2">
        <Input
          className="min-w-0 flex-1"
          label={t('auth.profile.name')}
          value={name ?? user.name ?? ''}
          onChangeText={setName}
        />
        <Button
          variant="primary"
          loading={busy}
          disabled={busy || name === null || name === user.name}
          webProps={{ type: 'submit' }}>
          {t('auth.profile.save')}
        </Button>
      </form>

      <Separator />

      <div className="flex flex-col gap-2">
        {/* flex-wrap + min-w-0: at 375px (the settings modal inlines this
            tab) a long email plus the button doesn't fit on one line — the
            button used to clip at the dialog edge. */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-0">
            <Label>{t('auth.profile.email')}</Label>
            <p className="break-all text-sm">
              {user.email}{' '}
              <span className="text-xs text-muted-foreground">
                {user.emailVerified
                  ? t('auth.profile.verified')
                  : t('auth.profile.unverified')}
              </span>
            </p>
          </div>
          {emailStep === 'idle' && (
            <Button
              variant="secondary"
              disabled={busy}
              onPress={() => setEmailStep('enter')}>
              {t('auth.profile.changeEmail')}
            </Button>
          )}
        </div>

        {emailStep === 'enter' && (
          <form onSubmit={submitEmailChangeRequest} className="flex items-end gap-2">
            <Input
              className="min-w-0 flex-1"
              label={t('auth.profile.newEmail')}
              value={newEmail}
              onChangeText={setNewEmail}
              webProps={{ type: 'email', autoComplete: 'email', required: true }}
            />
            <Button
              variant="primary"
              loading={busy}
              disabled={busy || !newEmail}
              webProps={{ type: 'submit' }}>
              {t('auth.profile.changeEmail')}
            </Button>
          </form>
        )}

        {emailStep === 'otp' && (
          <form
            onSubmit={confirmEmailChange}
            className="flex flex-col items-center gap-3">
            <p className="text-sm text-muted-foreground">
              {t('auth.profile.changeEmailSent', { email: newEmail })}
            </p>
            <CodeInput
              value={otp}
              onChangeText={setOtp}
              accessibilityLabel={t('auth.fields.code')}
            />
            <Button
              variant="primary"
              fullWidth
              loading={busy}
              disabled={busy || otp.length < 6}
              webProps={{ type: 'submit' }}>
              {t('auth.otp.verify')}
            </Button>
            <button
              type="button"
              className="text-sm text-primary-600 hover:text-primary-700 disabled:opacity-50"
              disabled={!resend.ready || busy}
              onClick={() => void requestEmailChangeOtp()}>
              {resend.ready
                ? t('auth.otp.resend')
                : t('auth.otp.resendIn', { seconds: resend.remaining })}
            </button>
          </form>
        )}
      </div>

      {errorKey ? (
        <p role="alert" className="text-sm text-destructive">
          {t(errorKey)}
        </p>
      ) : null}
    </div>
  );
}
